import React, { useEffect, useCallback, useState } from 'react';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { Box, Typography } from '@mui/material';
import { ensureArray } from 'BMapsUtil/js_utils';
import Dialog from './Dialog';
import { DialogCancelButton, DialogSubmitButton } from './DialogActionButtons';
import DialogHeader from './DialogHeader';

/**
 * @typedef {import('./Dialog').DialogProps} DialogProps
 */

let openConfirm = null;

export function registerConfirmOpen(fn) {
    openConfirm = fn;
}

/**
 * @param {string} title
 * @param {message[] | message | string[] | string | React.ReactElement[] | React.ReactElement} body
 * @param {Function} resolveFunction
 * @returns
 */
export function dialogConfirm(title, body, resolveFunction) {
    if (!openConfirm) {
        console.warn(`User Confirmation: ${title} - ${body}`);
        if (resolveFunction) resolveFunction(true);
        return null;
    }
    const formattedContent = formatDialogBody(body);
    return openConfirm({ title, body: formattedContent }, 'confirm', resolveFunction);
}

/**
 * @param {string} title
 * @param {message[] | message | string[] | string | React.ReactElement[] | React.ReactElement} body
 * @param {Function} resolveFunction
 * @returns
 */
export function dialogAlert(title, body, resolveFunction) {
    if (!openConfirm) {
        console.warn(`User Alert: ${title} - ${body}`);
        if (resolveFunction) resolveFunction(true);
        return null;
    }
    const formattedContent = formatDialogBody(body);
    return openConfirm({ title, body: formattedContent }, 'alert', resolveFunction);
}

export default function DialogProvider({ children }) {
    const [queue, setQueue] = useState([]);
    const current = queue[0];

    const open = useCallback((options={}, type, resolveFunction) => {
        setQueue((q) => q.concat([{
            body: options.body,
            title: options.title,
            type,
            resolve: resolveFunction,
        }]));
    }, []);
    useEffect(() => registerConfirmOpen(open), [open]);

    const resolveConfirmation = useCallback((result) => {
        setQueue((q) => {
            if (!q.length) return q;
            const [head, ...rest] = q;
            if (head.resolve) head.resolve(result);
            return rest;
        });
    }, []);

    const body = current?.body;

    return (
        <>
            {children}
            <Dialog
                open={Boolean(current)}
                onEscape={() => resolveConfirmation(false)}
                onClose={() => resolveConfirmation(false)}
                closeOnEscape
                transitionDuration={0}
                slotProps={{ backdrop: { transitionDuration: 0 } }}
                onKeyUp={(e) => {
                    if (e.key === 'Escape') {
                        e.stopPropagation();
                        resolveConfirmation(false);
                    }
                }}
            >

                {!!current?.title
                 && (
                 <DialogHeader
                     handleClose={() => resolveConfirmation(false)}
                     title={current.title}
                 />
                 )}
                <DialogContent sx={{ paddingBottom: '1rem' }}>
                    <Box sx={{ marginTop: '1rem', overflow: 'auto' }}>
                        {body}
                    </Box>
                </DialogContent>
                <DialogActions>
                    {current?.type === 'alert' && <AlertButtons resolveConfirmation={resolveConfirmation} />}
                    {current?.type === 'confirm' && <ConfirmButtons resolveConfirmation={resolveConfirmation} />}
                </DialogActions>
            </Dialog>
        </>
    );
}

function ConfirmButtons({ resolveConfirmation, confirmText='OK', cancelText='Cancel' }) {
    return (
        <Box sx={{
            display: 'flex', flexDirection: 'row', gap: '1rem', width: '100%',
        }}
        >
            <DialogSubmitButton
                onClick={() => resolveConfirmation(true)}
                sx={{ flexGrow: 1, minWidth: '14rem !important' }}
            >
                {confirmText}
            </DialogSubmitButton>
            <DialogCancelButton
                onClick={() => resolveConfirmation(false)}
                sx={{ flexGrow: 1, minWidth: '14rem !important' }}
            >
                {cancelText}
            </DialogCancelButton>
        </Box>
    );
}

function AlertButtons({ resolveConfirmation, confirmText='OK' }) {
    return (
        <Box sx={{
            display: 'flex', flexDirection: 'row', justifyContent: 'center', width: '100%',
        }}
        >
            <DialogSubmitButton
                onClick={() => resolveConfirmation(true)}
                sx={{ flexGrow: 1 }}
            >
                {confirmText}
            </DialogSubmitButton>
        </Box>
    );
}

/**
 * @typedef {{ header: string, fileNames: string[], footer: string}} message
 *
 * @param {message[] | message | string[] | string | React.ReactElement[] | React.ReactElement} msgs
 * @returns {React.ReactElement}
 */
export function formatDialogBody(msgs) {
    let messages = msgs;
    if (!Array.isArray(messages)) messages = [messages];

    const body = messages.map((message, msgI) => {
        if (React.isValidElement(message)) {
            return message;
        }
        const lines = ensureArray(message).flatMap((line) => {
            const myLines = [];
            if (typeof line === 'string') {
                for (const l of line.split('\n')) {
                    if (!l) {
                        myLines.push(<br />);
                    } else if (l.startsWith('* ')) {
                        myLines.push(<li style={{ marginLeft: '2em' }}>{l.slice(2)}</li>);
                    } else {
                        // dangerouslySetInnerHTML is used for <a>s in the BMaps About dialog.
                        // Populated by menu_cmds.js - inconvenient to add JSX there, punt on fix.
                        myLines.push(<span dangerouslySetInnerHTML={{ __html: l }} />);
                    }
                }
            } else {
                myLines.push(line);
            }
            return myLines;
        });
        return (
            <Box key={`dialog-msg-${msgI.toString()}`}>
                <Typography component="div" sx={{ margin: '.5rem 0' }}>
                    {lines.map((line, lineI) => (
                        <div key={`dialog-line-${lineI.toString()}`}>{line}</div>
                    ))}
                </Typography>
            </Box>
        );
    });
    return (
        <Box sx={{ lineHeight: '1.5', display: 'flex', flexDirection: 'column' }}>
            {body}
        </Box>
    );
}
